import ssmlGenerator from './ssml-generator';
import _ from 'lodash';
import postEffects from 'view/audioTracker/postEffects';
import Data from 'app-core/Data';
import { loadSpeechVoices, setVoicesPreventAutoLoad } from 'view/insertion/cognitive-arrays';

export default {
	computed: {
		inMemory() {
			return this.opts?.inMemory;
		},
		allTracks() {
			return this.loadedTracks.filter(f => !f.__isSubTrack).reduce((arr, track) => [...arr, track, ...track.subTracks], []);
		},
		creativeIsBusy() {
			return this.creative && (!!this.isLoading || !!this.isTracksLoading || !!this.isPublishing || !!this.loadingItems || !!this.trackerIsLoading);
		},
		isCreativeFile() {
			return this.creative?.JsonConfig?.isCreativeFile;
		},
		creativeIabCategories: {
			get() {
				return this.creative?.IabCategories || [];
			},
			set(v) {
				if (__DEBUG__)
					console.log('set creativeIabCategories', v);
			}
		},
		creativeIabCategoriesStr() {
			return this.creativeIabCategories.map(c => c.Name).join(', ') || '-';
		},
		translatedTracks: {
			get: function() { return this.$refs.tracker?.tracks?.filter(f => (f.insertion?.OriginalAudio || f.insertion?.OriginalSsml) && f.type === 'textToSpeech') || []; },
			cache: false
		},
		selfBindableOptions: {
			get: function() {
				const res = {};
				Object.keys(this).filter(f => !`${f}`.match(/[\_\$]/g) && f !== 'selfBindableOptions').map(f => {
					res[f] = this[f];
				});
				res['_refs'] = this.$refs;
				Object.keys(this._computedWatchers).filter(f => f !== 'selfBindableOptions').map(f => {
					Object.defineProperty(res, f, { get: () => this[f], enumerable: true });
				});
				return res;
			},
			cache: false
		},
		debugInfo() {
			return this.creative?.JsonConfig?.debugInfo;
		},
		isTemporary() {
			return this.creative?.IsTemporary;
		}
	},
	watch: {
		isTracksLoading(v) {
			if (v < 0)
				this.isTracksLoading = 0;
			this.calcDataIsLoaded();
		},
	},
	data() {
		return {
			creative: null,
			isEditingCreative: null,
			editingCreative: null,
			trackerIsLoading: 0,
			loadedTracks: [],
			visibleTracks: [],
			isLoading: 0,
			isTracksLoading: 0,
			loadedInsertions: null,
			loadingItems: 0,
			dataIsLoaded: false,
			dataIsLoadedFirst: false,
			editingAggregatedTrack: null,
			editingAggregatedTrackSubTrack: null,
			colorPickerOpened: false,
			isMixing: false,
			loadedCreativeVersion: null,
			creativeUpdatingState: 0,
			finalAudioBase64: null,
			isSavingCreative: false,
			dropDownOptions: { 
				width: 230,
				onShowing: this.onDropDownShowing
			}
		}
	},
	mounted() {
		setVoicesPreventAutoLoad(true);
	},
	methods: {
		onDropDownShowing(e) {
			this.$nextTick(() => {
				var content = e.component.content();
				let offset = content.getBoundingClientRect();

				content.style.setProperty('max-height', `${window.innerHeight - offset.top - 10}px`);
			});
		},
		makeCreativePersistent() {
			if (!this.isTemporary) return;

			this.isLoading++;
			this.$Data.postExternalApi('Service/CreateUpdateObjects', [{
				Id: this.creative?.Oid,
				Type: this.$Data.objectTypes.Creative,
				Fields: [{
					Name: 'IsTemporary',
					Value: false
				}]
			}]).then((r) => {
				if (r?.Success !== false) {
					this.$notifySuccess(this.$tCaption('Creative has successfully achieved a persistent state.'));
					this.creative.IsPersistent = false;
				}
			}).finally(() => this.isLoading--);
		},
		showDebugInfo() {
			if (!this.debugInfo) return;

			this.$refs.debugInfo.show(this.debugInfo);
		},
		async initCreative() {
			await this.removeCreativeLock();
			this.isEditingCreative = false;
			this.creative = null;
			this.dataIsLoaded = null;
			this.loadingItems = 0;
			this.isTracksLoading = 0;
			this.visibleTracks = [];
			this.autoPublishInitiated = false;
			this.isPublished = null;
			this.publicationStatistic = null;
			this.loadCreative()
				.then(() => {
					if (this.isGenerating) return;
					this.checkCreativeEditingLock(true);
					this.loadInsertions()
						.then(() => {
							setImmediate(() => {
								if (this._isDestroyed) return;
								this.checkVersionUpdate();
							});
						});
					
					loadSpeechVoices(void 0, this.creative?.Language?.Code);
				});
			this.hasPublishHistory = null;
			if (!this.isPublishMode) {
				this.calcHasPublishHistory();
				postEffects.getPlugins(true);
				this.checkPublicationStatisticProgress();
			}
		},
		async loadCreativeJson() {
			if (this.opts?.creative) return this.opts?.creative.JsonConfig;
			return this.$Data.getExternalApi('creative/GetCreativeJson', `creativeId=${this.creative?.Oid}`).then(r => {
				if (r?.ExceptionType) return;
				let aggregatedTracks = r?.aggregatedTracks || [];
				if (!aggregatedTracks?.length) {
					aggregatedTracks = [{ trackNumber: 0 }];
				}
				aggregatedTracks = this.creative?.JsonConfig?.aggregatedTracks || aggregatedTracks.map(f => this.prepareAggregatedTrack(f));
				this.loadedCreativeVersion = r?.creativeVersion || 0;
				this.creative.JsonConfig = {
					creativeVersion: 0,
					editingLock: {
						utcTimestamp: null,
						userId: null,
						userName: null
					},
					...r,
					templateFields: this.creative?.JsonConfig?.templateFields || r?.templateFields || [],
					voiceoverRequests: this.creative?.JsonConfig?.voiceoverRequests || r?.voiceoverRequests || [],
					excelData: this.creative?.JsonConfig?.excelData || r?.excelData || [],
					masterTrackConfig: this.prepareEffectsConfig(r?.masterTrackConfig),
					aggregatedTracks,
					sonicScribe: this.creative?.JsonConfig?.sonicScribe || r?.sonicScribe || {}
				}
			})
		},
		async loadCreative() {
			const processCreative = (r) => {
				let aggregatedTracks = r?.JsonConfig?.aggregatedTracks || [];
					if (!aggregatedTracks?.length) {
						aggregatedTracks = [{ trackNumber: 0 }];
					}
					aggregatedTracks = aggregatedTracks.map(f => this.prepareAggregatedTrack(f));

					if (this.creative && this.creative.Oid === this.creativeId) {
						if (__DEBUG__)
							console.debug('Reload creative');
						_.assign(this.creative, { 
							...r,
							JsonConfig: {
								...(r?.JsonConfig || {}),
								templateFields: this.creative?.JsonConfig?.templateFields || r?.JsonConfig?.templateFields || [],
								voiceoverRequests: this.creative?.JsonConfig?.voiceoverRequests || r?.JsonConfig?.voiceoverRequests || [],
								excelData: this.creative?.JsonConfig?.excelData || r?.JsonConfig?.excelData || [],
								masterTrackConfig: this.creative?.JsonConfig?.masterTrackConfig || this.prepareEffectsConfig(r?.JsonConfig?.masterTrackConfig),
								aggregatedTracks
							}
						});
					} else {
						if (__DEBUG__)
							console.debug('Load creative', { r });
						this.loadedCreativeVersion = r.JsonConfig?.creativeVersion || 0;
						this.creative = this.prepareCreative({
							...r,
							JsonConfig: {
								creativeVersion: 0,
								editingLock: {
									utcTimestamp: null,
									userId: null,
									userName: null
								},
								...(r?.JsonConfig || {}),
								templateFields: this.creative?.JsonConfig?.templateFields || r?.JsonConfig?.templateFields || [],
								voiceoverRequests: this.creative?.JsonConfig?.voiceoverRequests || r?.JsonConfig?.voiceoverRequests || [],
								excelData: this.creative?.JsonConfig?.excelData || r?.JsonConfig?.excelData || [],
								masterTrackConfig: this.prepareEffectsConfig(r?.JsonConfig?.masterTrackConfig),
								aggregatedTracks
							}
						});

						this.$emit('creative-initialized', this.creative);
					}
					this.creativeUpdatingState = 0;
					this.$setTitle(this.creative.Name);
					if (this.creative?.IsPublished)
						this.isPublished = true;

					return this.creative;
			};
			if (this.opts?.creative) {
				return processCreative(this.opts?.creative);
			}
			return Data
				.getExternalApi('creative/GetCreative', `creativeId=${this.creativeId}`)
				.then(r => processCreative(r));
		},
		async loadInsertions() {
			if (this.isPublishing) return;
			const processInsertions = (results, setIsLoaded) => {
				results = results || [];
				this.loadedInsertions = results;
				if (!(this.$auth.hasCreativeApi() || this.forceTracker)) return results;
				let tracks = [];
				results
					.filter(f => !f.ParentId)
					.map(f => {
						let track = this.getTrackFromInsertion(f, true);

						if (setIsLoaded) {
							track.__isInsertionLoaded = true;
						}

						tracks.push(track);
						this.checkSsmlTrack(track);
						track.insertion.SubInsertions = [];
						track.subTracks = [];

						if (track.__totalSubInsertions) {
							track.__initSubTracks();
						}

						if (this.opts?.__regenerateInsertions && track.__regenerate) {
							track.__regenerate();
						}
					});

				this.loadedTracks = tracks;
				return results;
			}
			if (this.opts?.insertions) {
				processInsertions(this.opts?.insertions);
				this.calcDataIsLoaded();
				return;
			}
			this.isLoading++;
			this.loadedInsertions = null;
			this.loadingItems++;
			return Data
				.getInsertionsByCreative(this.creativeId)
				.then(r => {
					if (__DEBUG__)
						console.info('Insertions loaded', { r });
					return processInsertions(r.Results, true);
				})
				.finally(() => {
					this.isLoading--;
					this.loadingItems--;
					if (__DEBUG__)
						console.debug('Calc data is loaded on load insertions');
					this.calcDataIsLoaded();
					if (!this.$auth.hasCreativeApi()) return;
				});
		},
		onTrackLoading() {
			this.isTracksLoading = this.loadedTracks.filter(f => f.__isLoading).length;
		},
		calcDataIsLoaded() {
			setImmediate(() => {
				if (this._isDestroyed) return;
				this.dataIsLoaded = this.isCreativeFile || (!!this.$refs.tracker && this.loadingItems === 0 && this.isTracksLoading === 0 && !(this.$refs.tracker.visibleTracks.some(f => !f.__isSrcLoaded)));
				if (__DEBUG__)
					console.debug('Calc data is loaded', this.dataIsLoaded/*, {
						items: this.loadingItems,
						loadingTracks: this.isTracksLoading,
						tracker: this.$refs.tracker,
						tracks: this.$refs.tracker?.tracks.filter(f => !f.__isSrcLoaded)
					}*/);
			});
		},
		onCreateTemplateField(templateField, callback) {
			templateField.id = this.$Data.getNextNaming(templateField.id, (this.creative.JsonConfig.templateFields || []).map(f => f.id));
			templateField.description = this.$Data.getNextNaming(templateField.description, (this.creative.JsonConfig.templateFields || []).map(f => f.description));
			this.creative.JsonConfig.templateFields.push(templateField);
			this.$notifySuccess(this.$tCaption('Template field has been successfully generated'));

			if (callback) {
				callback(templateField);
			}
		},
		updateTags(tags, excludeTracksReloading) {
			if (__DEBUG__)
				console.log('Update tags', { tags, excludeTracksReloading });
			this.isLoading++;
			this.$Data.postExternalApi('Creative/UpdateTagsInInsertions', {
				CreativeId: this.creative.Oid,
				AdthosTags: tags
			}).then(r => {
				if (r?.Data?.length) {
					const items = r?.Data?.filter(f => !excludeTracksReloading?.includes(f));
					if (items.length) {
						this.reloadTracks(items);
					}
				}
			}).finally(() => this.isLoading--);
		},
		reloadTracks(trackIds) {
			if (__DEBUG__)
				console.debug('Reload tracks', { trackIds });
			this.allTracks.filter(f => trackIds.includes(f.id)).map(f => {
				f.__isInsertionLoaded = false;
				f.__ensureTrackLoaded(true).then(() => {
					if (f.__regenerate)
						f.__regenerate();
				});
			});
		},
		regenerateInsertion(ids) {
			this.allTracks
				.filter(f => (Array.isArray(ids) ? ids.includes(f.id) : f.id === ids))
				.map(track => track.__regenerate && track.__regenerate());
		},
		onTrackActionsClick(e) {
			if (__DEBUG__)
				console.debug('onTrackActionsClick', { e });
			e?.event?.stopPropagation();
			e?.event?.preventDefault();
		},
		onAggregatedTrackReorder(track, currentIndex, newIndex) {
			this.creative.JsonConfig.aggregatedTracks.splice(currentIndex, 1);
			this.creative.JsonConfig.aggregatedTracks.splice(newIndex, 0, track);
		},
		onSetSoloSubTracks(tracks) {
			if (__DEBUG__)
				console.debug('onSetSoloSubTracks', { tracks });
			(tracks || []).filter(f => f.type === 'textToSpeech' && !f.insertion?.Audio && !f.__isLoading && f.insertion?.Ssml).map(f => {
				ssmlGenerator.generateAndSaveSSMLAudio(this, f, true);
			});
		},
		async mixAndDownloadFinalAudio() {
			const replacedVoices = await this.checkDefaultReplacedVoices();
			if (__DEBUG__)
				console.debug('Mix and download', { replacedVoices });
			if (replacedVoices) {
				this.$notifyWarning(this.$tCaption('We activating voices, please wait'));
				this.isMixing = true;
				return;
			}

			this.isMixing = true;
			this.$eventsBus.$emit('notification-message', {
				message: this.$tCaption('Starting making final audio for creative (CREATIVE_NAME).').replace('CREATIVE_NAME', this.creative?.Name)
			});
			this.$eventsBus.$emit('notification-busy', true);
			await this.checkRevoicingDynamicData();
			this.$refs.tracker?.downloadFinalAudio()
				.then(() => {
					this.$eventsBus.$emit('notification-message', {
						message: this.$tCaption('The final audio for creative (CREATIVE_NAME) has been created.').replace('CREATIVE_NAME', this.creative?.Name)
					});
				})
				.finally(() => {
					this.isMixing = false;
					this.$eventsBus.$emit('notification-busy', false);
				});
		},
		makeCreativeCopy() {
			this.isLoading++;
			this.$Data.cloneCreative(this.creative.Oid, this.creative.Name, true).finally(() => this.isLoading = false);
		},
		setEditingAggregatedTrack(aggTrack, timeout, track) {
			setTimeout(() => {
				if (this._isDestroyed) return;
				if (!aggTrack && this.colorPickerOpened) return;
				if (aggTrack) {
					this.editingAggregatedTrack = aggTrack;
					this.editingAggregatedTrackSubTrack = track;
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.$refs.trackNameBox.instance.focus();
						this.$refs.trackNameBox?.$el?.querySelector('input')?.select();
					});
				} else {
					this.editingAggregatedTrack = null;
					this.editingAggregatedTrackSubTrack = null;
				}
			}, timeout || 0);
		},
		openColorSelector(aggTrack) {
			if (__DEBUG__)
				console.debug('Open color selector');
			this.colorPickerOpened = true;
			setImmediate(() => {
				if (this._isDestroyed) return;
				setImmediate(() => {
					this.$refs[`colorBox_${aggTrack.trackNumber}`]?.instance?.open();
				});
			});
		},
		getMaxTrackNumber(inc) {
			let idx = 0;
			const check = () => {
				idx = (_.maxBy(this.creative.JsonConfig.aggregatedTracks, v => v.trackNumber)?.trackNumber || 0) + inc;
				return this.loadedTracks.find(f => f?.insertion?.JsonConfig?.trackNumber === idx);
			}

			while (check()) {
				inc++;
			}

			return idx;
		},
		createNewAggregatedTrack(opts) {
			let aggTrack = this.prepareAggregatedTrack({ trackNumber: this.getMaxTrackNumber(1) });
			if (opts?.insertAfter) {
				const idx = this.creative.JsonConfig.aggregatedTracks.findIndex(f => f === opts.insertAfter || f.trackNumber === opts.insertAfter);
				this.creative.JsonConfig.aggregatedTracks.splice(idx + 1, 0, aggTrack);
			} else {
				this.creative.JsonConfig.aggregatedTracks.push(aggTrack);
			}
			
			this.$refs.tracker.focusedAggregatedTrack = aggTrack;
			return aggTrack;
		},
		hasAppliedCustomSettings(track, aggTrack) {
			return track?.__aggregatedTrack?.wapiPlugins?.some(f => f.enabled) || aggTrack?.normalize?.enabled || aggTrack?.condition;
		},
		hasAppliedCustomTrackSettings(track) {
			return track?.insertion?.JsonConfig?.fadeIn?.value || track?.insertion?.JsonConfig?.fadeOut?.value || track?.insertion?.JsonConfig?.volumeEnvelope?.enabled
				|| track?.insertion?.JsonConfig?.pitchEnvelope?.enabled;
		},
		hasAppliedClipSettings(track) {
			return (track?.pitchRate || 1) !== 1 || (track?.playbackRate || 1) !== 1 || (track?.volumeRate || 1) !== 1;
		},
		onTrackDblClick(track) {
			if (__DEBUG__)
				console.debug('On track dbl click', { track });
			this.editInsertion(track);
		},
		onTracksChanged(changedTracks, finishCallback) {
			if (this.isTracksLoading || this.isPublishMode) return;
			changedTracks.map(track => {
				if (!track.__isInsertionLoaded || !track.__hasChanges()) return;
				
				if (!track.__skipAddToHistory)
					track.__addDataToUndoHistory();
				else
					track.__skipAddToHistory = false;

				if (__DEBUG__)
					console.debug('On track changed', { track });

				if (!track.__isSubTrack)
					track.insertion.StartTime = parseInt(track.start * 1000);

				if (this.$refs?.tracker?.activeToolId) return;

				if (__DEBUG__)
					console.debug('OnTracksChanged');
				return this.saveTrackInsertion(track, 'onTracksChanged');
			});
			if (finishCallback)
				finishCallback();
		},
		saveTrackInsertion(track, log, force) {
			if (this.inMemory || this.isPublishing || this.isPublished) return;
			if (__DEBUG__)
				console.debug('Save track insertion', { log, track, readonly: this.readonly, __isInsertionLoaded: track.__isInsertionLoade, insertionOid: track?.insertion?.Oid })
			if (!force && (this.readonly || !track.__isInsertionLoaded)) return Promise.resolve(track?.insertion);
			if (!track?.insertion?.Oid) return Promise.resolve(track?.insertion);

			if (track.__isSaving) {
				track.__isSaving++;
				return;
			}

			track.__isLoading++;
			track.__isSaving++;
			return this.$Data.createUpdatePureInsertion(track.insertion).then(r => {
				if (__DEBUG__)
					console.info('Insertion created/updated', r);
				return r;
			}).finally(() => {
				track.__isLoading--;
				track.__isSaving--;

				if (track.__isSaving > 0) {
					track.__isSaving = 0;
					this.saveTrackInsertion(track, log, force);
				}
			});
		},
		processAddTrack(track, preventTotal) {
			if (__DEBUG__)
				console.debug('Process add track', { track, preventTotal, loadedTracks: this.loadedTracks });
			if (_.find(this.allTracks, { id: track.id })) return;
			if (track && track.__isSubTrack) {
				let parentTrack = this.loadedTracks.filter(f => f.id === track.insertion.ParentId)[0];
				if (parentTrack) {
					parentTrack.subTracks.push(track);
					if (!preventTotal) {
						parentTrack.__totalSubInsertions++;
					}
				}
			} else {
				this.checkAggregatedTrackExists();
				this.loadedTracks.push(track);
			}

			this.$eventsBus.$emit('tracker-new-track', track);
		},
		onInsertionSetTrack(data) {
			if (__DEBUG__)
				console.debug('On insertion add track', { data });
			if (data.isNew) {
				let newTrack = data?.track || this.getTrackFromInsertion(data.insertion);
				newTrack.__isSubTrack = data.track.__isSubTrack;

				this.processAddTrack(newTrack);
			} else {
				if (data.track) {
					let track = this.loadedTracks.find(v => v?.id === data?.track?.id) || data.track;
					delete data.insertion.SubInsertions;
					_.merge(track.insertion, data.insertion);
					let trackData = this.getTrackFromInsertion(data.insertion);
					_.merge(track, trackData);
					if (__DEBUG__)
						console.debug('set track', { data, d1: data.insertion.Duration, d2: trackData.duration });
				}
			}
			if (this.$refs.tracker && data.trackId) {
				this.$refs.tracker.setBuffer(data.trackId, data.buffer, data.base64);
			}

			this.calcVisibleTracks();
		},
		getTrackIcon(track) {
			let type = _.find(this.insertionKinds, v => v.id === track.type);
			return type && type.icon;
		},
		calcVisibleTracks() {
			let idx = 0;
			this.visibleTracks = this.loadedTracks.filter(f => !f.__isDeleted).reduce((arr, item) => {
				item.__sortIndex = idx++;
				arr.push(item);
				item.subTracks.map(st => {
					st.__sortIndex = idx++;
					arr.push(st);
				});
				return arr;
			}, []);
			this.checkSimulateRulesAllTracks();
			this.onTrackLoading();
		},
		prepareEffectsConfig(data, additional) {
			return this.mergeDestPropsOnly({
				panner: 0,
				postEffects: _.merge({
					enabled: false,
					levelIn: 1,
					levelOut: 1,
					plugins: []
				}, data?.postEffects || {}),
				compressor: {
					attack: 0.003,
					knee: 20.0,
					ratio: 5.0,
					release: 0.25,
					threshold: -71.0,
					compressorMakeUpGain: 1,
					enabled: false,
					compressorInputGain: 1,
					compressorOutputGain: 1
				},
				equalizer: [],
				volume: 1,
				muted: false,
				wapiPlugins: data?.wapiPlugins || [],
				...(additional || {})
			}, data);
		},
		prepareAggregatedTrack(data) {
			let aggTrack = this.mergeDestPropsOnly({
				trackNumber: 0,
				color: null,
				name: `Track ${(data?.trackNumber || 0) + 1}`,
				selectedPluginBank: null,
				condition: null,
				__type: null,
				normalize: {
					enabled: false,
					value: -3,
					method: 'peak'
				},
				__internallyMuted: false,
				...this.prepareEffectsConfig(data)
			}, data);

			aggTrack.__internallyMuted = false;

			Object.defineProperty(aggTrack, '__isSolo', {
				get: () => {
					return this.$refs?.tracker?.soloTrackId === aggTrack.trackNumber;
				}
			});
			Object.defineProperty(aggTrack, '__tracks', {
				get: () => {
					return (this.loadedTracks || []).filter(f => f?.trackNumber() === aggTrack.trackNumber);
				}
			});
			Object.defineProperty(aggTrack, '__readonly', {
				get: () => {
					return this.readonly && (!(this.userRestrictedAccess?.aggregatedTracks || []).includes(aggTrack.trackNumber) || (this.creativeIsLockedByOtherUser || !this.dataIsLoadedFirst || this.creative?.IsPublished));
				}
			});

			this.defineAggregatedTrackUndoRedoMethods(aggTrack);
			return aggTrack;
		},
		onDurationChange(v) {
			if (!this.creative.Length && v !== this.creative.Length) {
				this.creative.Length = parseInt(v);
				this.$updateCreative('onDurationChange');
			}
		},
		async processEditingCreative(e) {
			if (e?.validationGroup && !e?.validationGroup?.validate().isValid) {
				return;
			}
			if (this.isEditingCreative && !this.readonly) {
				const rootNameIsSame = this.creative.RootCreativeName === this.creative.Name;
				_.assign(this.creative, _.omit(this.editingCreative, ['JsonConfig']));
				const oldTemplateFields = this.creative.JsonConfig.templateFields || [];
				this.creative.JsonConfig.templateFields = this.editingCreative.JsonConfig.templateFields || [];
				this.$set(this.creative.JsonConfig, 'adjustLengthToTemplate', this.editingCreative.JsonConfig?.adjustLengthToTemplate);
				this.$set(this.creative.JsonConfig, 'newsMLTopHeadline', this.editingCreative.JsonConfig?.newsMLTopHeadline);
				this.$set(this.creative.JsonConfig, 'newsMLTopContent', this.editingCreative.JsonConfig?.newsMLTopContent);
				if (rootNameIsSame) {
					this.creative.RootCreativeName = this.creative.Name;
				}
				this.$refs.header.$refs.creativeDetail.save();
				this.isSavingCreative = true;
				await this.updateCreative('processEditingCreative').then(() => this.$refs.header.$refs.creativeDetail.regenerateInsertionsByChangedTemplateFields(oldTemplateFields, this.creative.JsonConfig.templateFields))
					.finally(() => this.isSavingCreative = false);
			}
			this.isEditingCreative = !this.isEditingCreative;
			this.editingCreative = _.cloneDeep(this.creative);
			this.editingCreative.JsonConfig = _.pick(this.editingCreative.JsonConfig, ['templateFields', 'adjustLengthToTemplate', 'newsMLTopHeadline', 'newsMLTopContent']);
		},
		cancelEditingCreative() {
			this.isEditingCreative = !this.isEditingCreative;
		},
		deleteEditingCreative() {
			let creative = this.creative;
			this.$confirmMessage({
				title: this.$tCaption('Confirm action', '@actions'),
				text: this.$tCaption('Are you sure you want to delete the current creative?'),
				handler: () => {
					this.$set(this.creative, '__isDeleting', true);
					Data.getExternalApi('Creative/DeleteCreative', `creativeId=${this.creativeId}`).then(() => {
						this.$notifySuccess(this.$tCaption('Creative deleted'));
						this.creative = null;
						if (creative?.RootCreativeId && creative?.RootCreativeId !== creative?.Oid) {
							this.$router.push({ path: `/creative/Detail/${creative.RootCreativeId}` });
						} else {
							if (creative.IsTemplate && creative.IsProduct) {
								this.$router.push({ name: 'products-creatives-templates-list' });
							} else {
								this.$router.push({ path: '/creative/List' });
							}
						}
					});
				}
			});
		},
		checkAggregatedTrackExists() {
			if (!this.creative?.JsonConfig?.aggregatedTracks?.length) {
				this.creative?.JsonConfig?.aggregatedTracks.push(this.prepareAggregatedTrack({ trackNumber: 0 }));
			}
		},
		prepareCreative(creative) {
			this.defineCreativeUndoRedoMethods(creative);
			if (!creative.hasOwnProperty('__totalSubInsertions')) {
				Object.defineProperty(creative, '__totalSubInsertions', {
					get: () => this.allTracks.reduce((total, track) => total += track.__totalSubInsertions || 0, 0)
				})
			}
			if (!creative.hasOwnProperty('__save')) {
				Object.defineProperty(creative, '__save', {
					get: () => this.$updateCreative
				})
			}
			if (!creative.hasOwnProperty('__saveJson')) {
				Object.defineProperty(creative, '__saveJson', {
					get: () => this.$updateCreativeJson
				})
			}
			return creative;
		},
		async updateCreativeJson(force) {
			if (this.inMemory) return;
			if (!this.dataIsLoaded && !force && this.creative?.__isDeleting) return;
			if (!force && (!this.creative || this.isPublishMode || this._isDestroyed || this.isGenerating)) return;
			if (!force && this.creativeIsLockedByOtherUser) return;
			this.isLoading++;
			return this.$Data.postExternalApi('creative/UpdateCreativeJson', {
				Oid: this.creative?.Oid,
				JsonConfig: this.creative?.JsonConfig
			}).finally(() => this.isLoading--);
		},
		onTotalDurationChanged(v) {
			if (this.isTracksLoading) return;
			if (this.creative.Duration !== parseInt(v * 1000)) {
				if (__DEBUG__)
					console.debug('Total duration changed', {
						new: parseInt(v * 1000),
						old: this.creative.Duration
					});
				this.creative.Duration = parseInt(v * 1000);
				this.$updateCreative('onTotalDurationChanged');
			}
		},
		onMasterTrackConfigChanged(config) {
			if (__DEBUG__)
				console.debug('On master track config changed', { config });
			this.creative.JsonConfig.masterTrackConfig = config;
		},
		updateCreative(initiator, force) {
			if (this.isGenerating || this.inMemory) return Promise.resolve(this.creative);
			if (this.creativeUpdatingState !== 0) {
				this.creativeUpdatingState = 2;
			}
			if (!this.dataIsLoaded && !force && this.creativeUpdatingState !== 0 || this.creative?.__isDeleting) return;
			if (__DEBUG__)
				console.log('Update creativeUpdate creative', initiator, {
					dataLoaded: this.dataIsLoaded,
					force
				});
			if (!this.creative || this.isPublishMode || this._isDestroyed) return;
			this.isLoading++;
			this.creativeUpdatingState = 1;
			return this.$Data
				.createUpdateCreative(this.creative)
				.finally(() => {
					if (this._isDestroyed) return;
					this.isLoading--;

					if (this.creativeUpdatingState > 1) {
						this.creativeUpdatingState = 0;
						this.$updateCreative(initiator);
					} else {
						this.creativeUpdatingState = 0;
					}
				});
		},
		showNewRevisionPopup() {
			if (this.$refs.header.$refs.revisions) {
				this.$refs.header.$refs.revisions.showNewRevisionPopup();
			}
		},
		showNewLanguagePopup() {
			if (this.$refs.header.$refs.revisions) {
				this.$refs.header.$refs.revisions.showNewLanguagePopup();
			}
		}
	}
};
